import type { GridMode } from "@lancer-vtt/shared";
import type { Hex, Square } from "@lancer-vtt/space";
import { axial, hex, square } from "@lancer-vtt/space";

/**
 * The tagged hex/square position union every command and stored position speaks, plus
 * its constructors and grid-match guards.
 */

/**
 * A **hex** position — cube coords (`{q,r,s}`, the `Hex` shape) plus the `grid`
 * discriminator. The hex arm of the {@link Cell} tagged union.
 */
export type HexCell = Hex & { grid: "hex" };

/**
 * A **square** position — integer lattice (`{x,y}`, the `Square` shape) plus the
 * `grid` discriminator. The square arm of the {@link Cell} tagged union.
 */
export type SquareCell = Square & { grid: "square" };

/**
 * A position on the encounter's map — the **tagged union** the spatial port targets.
 * Self-describing on the wire (`grid` names the coordinate system), so the reducer can
 * assert a position matches its map's grid ({@link cellGridMismatch}) and the union
 * extends cleanly to further coordinate backends. Deliberately a tag, not a structural
 * `{q,r,s}|{x,y}` sniff.
 *
 * **The grid is keyed off the encounter, not the cell, for geometry.** The engine
 * binds one `World<Cell>` (chosen by `state.gridMode`, via {@link ../world worldFor})
 * and never reads a cell's `grid` to pick geometry — `grid` is the *self-describing
 * wire tag* + the reducer's grid-match assertion, not a per-call geometry switch.
 * Cells the *world geometry* returns are structurally the right shape but may not
 * carry the tag at runtime (the controlled port cast in `world.ts`); only *driver-
 * authored* cells (a `move_token.to`, a parsed CLI cell) are guaranteed tagged, and
 * those are the only ones the assertion gates.
 */
export type Cell = HexCell | SquareCell;

/** Construct a tagged {@link HexCell} (delegates to the space constructor for the `q+r+s=0` invariant + `-0` normalization). */
export function hexCell(q: number, r: number, s?: number): HexCell {
  const h = s === undefined ? axial(q, r) : hex(q, r, s);
  return { grid: "hex", q: h.q, r: h.r, s: h.s };
}

/** Construct a tagged {@link SquareCell} (delegates to the space constructor for `-0` normalization). */
export function squareCell(x: number, y: number): SquareCell {
  const sq = square(x, y);
  return { grid: "square", x: sq.x, y: sq.y };
}

/** The grid family a {@link GridMode} addresses positions in — square for `2d-square`, hex otherwise. */
export function gridFamily(mode: GridMode): "hex" | "square" {
  return mode === "2d-square" ? "square" : "hex";
}

/**
 * Tag a bare space coordinate with the grid family the encounter addresses (used at
 * the driver→domain seam — the CLI/UI parse a key through the world, then stamp the
 * matching `grid` so the stored/wire cell is self-describing). `raw` is structurally
 * the matching coordinate (`{q,r,s}` for hex, `{x,y}` for square).
 */
export function tagCell(mode: GridMode, raw: Hex | Square): Cell {
  return gridFamily(mode) === "square"
    ? { grid: "square", ...(raw as Square) }
    : { grid: "hex", ...(raw as Hex) };
}

/**
 * Narrow a {@link Cell} to a `Hex` for a **hex-only** reader (the fenced display
 * surfaces: the `/scratch/map` editor, `frontend/src/lib/hexMap/*`, scene presets).
 * A `HexCell` *is* a `Hex` structurally; this throws loudly only if a square cell
 * reaches a hex-only surface (square map editing is a separate future surface — it
 * must never silently render as hex). Tolerant of an untagged-at-runtime hex cell
 * (a world-geometry cell): only a `grid: "square"` tag is rejected.
 */
export function asHex(cell: Cell): Hex {
  if ("grid" in cell && cell.grid === "square") {
    throw new Error(`asHex: a square cell reached a hex-only surface (${JSON.stringify(cell)})`);
  }
  return cell;
}

/**
 * Assert a *driver-authored* {@link Cell} matches the encounter's grid family,
 * returning a `CommandError`-able message when it doesn't (the reducer is the wall:
 * a hex `move_token.to` can never strand in a square encounter, and vice versa).
 * Tolerant of an untagged cell (a world-geometry cell carries no `grid` at runtime
 * and is trusted as already on-grid) — only an explicit *mismatching* tag is
 * rejected. `null` when the cell is on-grid (or untagged).
 */
export function cellGridMismatch(mode: GridMode, cell: Cell): string | null {
  if (!("grid" in cell) || cell.grid === undefined) return null;
  const want = gridFamily(mode);
  if (cell.grid !== want) {
    return `position is a ${cell.grid} cell but the encounter grid is ${mode} (${want})`;
  }
  return null;
}
